/**
 * Converts a stored compositor to the resource shape.
 *
 * A "wire" below means a connection from another node into one of the
 * compositor's inputs. A layer on a wire shows whatever that node produces. The
 * other case is an uploaded file, which the compositor owns.
 *
 * **Do not change the rules here for a shape that already exists.** Documents in
 * every one of those shapes are still out there, and a changed rule gives them a
 * different answer than the one an earlier load already gave them. Adding a
 * branch for a new shape is fine. Nothing outside this file should call this, so
 * that all the answers stay in one place.
 */
import type { AudioFile, ImageFile, InputId, Resource, ResourceId, VideoFile } from 'web';
import type { CompositorData } from '@/types/nodes/compositor';

/** A text layer, which keeps its value on itself and is never a resource. */
type TextKind = { type: 'text'; value?: { type: 'input'; data: InputId } | { type: 'value'; data: string } };

/**
 * Everything a stored media layer might be carrying, new and old at once. It has
 * `resource` because this also runs on documents it converted before, where the
 * layer holds the reference and whatever shape it had before that.
 */
export type StoredMediaKind = {
  type: 'image' | 'video' | 'audio';
  resource?: ResourceId;
  content?: { type: 'value'; data: ImageFile | VideoFile | AudioFile } | { type: 'input'; data: InputId };
  inputId?: InputId;
  file?: ImageFile | VideoFile | AudioFile;
};

/**
 * How an uploaded file is recognized across two resources. The asset's own id
 * answers when both files carry one, because a url is a locator that the same
 * file can have several of, and export rewrites it. A blank id counts as no id
 * at all, otherwise every file without one would look like the same file.
 *
 * A file that carries an id is never matched by url. Comparing across the two
 * would not be transitive: an id-less file at the same url as two files with
 * different ids would match both, and those two would then end up sharing one
 * entry even though they are different assets.
 */
function holdsSameFile(left: Resource, right: Resource): boolean {
  if (left.type !== right.type || left.content.type !== 'value' || right.content.type !== 'value') {
    return false;
  }
  const leftId = left.content.data.id || undefined;
  const rightId = right.content.data.id || undefined;
  if (leftId !== undefined || rightId !== undefined) {
    return leftId === rightId;
  }
  return left.content.data.url === right.content.data.url;
}

/**
 * Gives every media layer in a stored compositor a resource, and returns the
 * table those layers point at. The layers are mutated in place, so the store
 * ends up holding the same objects and the whole document is normalized without
 * an edit.
 *
 * Written to run on a document in any shape, and more than once on the same
 * document. **The presence of `resources` proves nothing about the layers below
 * it.** A client that predates resources preserves the field untouched while
 * writing an edited layer back with its content inline, so a document can come
 * back with a full table and legacy-shaped layers mixed together. Every layer is
 * therefore decided on its own, with no "already migrated, skip the rest" test
 * at the top.
 *
 * The two reuse rules are load-bearing:
 *
 * - **A wired layer reclaims the entry for its wire**, whatever type that entry
 *   holds. At most one entry exists per wire, and the engine's reconciliation
 *   depends on that. In the mixed case above, the wire may have started
 *   producing video while the table still holds an image for it, and matching on
 *   type alone would give one wire two entries.
 * - **An uploaded file reuses an entry already holding it.** Duplicating or
 *   pasting a layer clones the file inline, so documents exist with several
 *   layers holding one file. Merging them makes a migrated document agree with a
 *   document where the same duplicate happened after this shipped, which
 *   produces one resource used twice.
 */
export function migrateCompositorResources(compositor: CompositorData): Record<ResourceId, Resource> {
  const resources: Record<ResourceId, Resource> = { ...(compositor.resources ?? {}) };
  const wires = new Map<InputId, ResourceId>();
  for (const [id, resource] of Object.entries(resources)) {
    if (resource.content.type === 'input') {
      wires.set(resource.content.data, Number(id));
    }
  }
  // Same rule as the engine's `Compositor::add_resource`: after every slot ever
  // used, never one a delete has freed. The assets panel lists content in id
  // order, so filling a hole would put this entry in the middle of the list.
  let highestId = Math.max(-1, ...Object.keys(resources).map(Number));
  const nextId = (): ResourceId => (highestId += 1);

  // Wires that a text layer answers for, and entries some layer still points at.
  // Both are needed for the cleanup at the end.
  const textWires = new Set<InputId>();
  const referenced = new Set<ResourceId>();

  // Integer-like object keys iterate in ascending numeric order, so this visits
  // the layers in a stable order and reaches every one of them, including a
  // layer that `layerOrder` has lost.
  for (const [layerId, layer] of Object.entries(compositor.layers)) {
    if (layer.kind.type === 'shape' && layer.fill?.type === 'image') {
      if (resources[layer.fill.resource]?.type === 'image') {
        referenced.add(layer.fill.resource);
      } else {
        // A fill pointing at nothing usable is dropped rather than failing the
        // load: the shape keeps its geometry and stroke, and shows no fill.
        layer.fill = { type: 'none' };
      }
    }
    const kind = layer.kind as StoredMediaKind;
    if (kind.type !== 'image' && kind.type !== 'video' && kind.type !== 'audio') {
      if (kind.type === 'text') {
        const value = (kind as unknown as TextKind).value;
        if (value?.type === 'input') {
          textWires.add(value.data);
        }
      }
      continue;
    }

    // A reference that still answers, with an entry of the same media type, is
    // this document's own answer. Keeping it is what makes the fold safe to run
    // again: re-deriving ids from scratch would renumber the table and leave
    // every layer converted by an earlier pass pointing at the wrong entry.
    const existing = kind.resource !== undefined ? resources[kind.resource] : undefined;
    if (existing !== undefined && existing.type === kind.type) {
      if (existing.content.type === 'input') {
        wires.set(existing.content.data, kind.resource as ResourceId);
      }
      referenced.add(kind.resource as ResourceId);
      continue;
    }

    // The three older shapes, newest first, so a layer carrying more than one
    // reads as its newest. A file wins over a stray wire, matching the export
    // reader and the two-field reads that came before it.
    const content =
      kind.content ??
      (kind.file !== undefined
        ? ({ type: 'value', data: kind.file } as const)
        : kind.inputId !== undefined
          ? ({ type: 'input', data: kind.inputId } as const)
          : undefined);
    if (content === undefined) {
      // A layer with nothing at all is broken. Fail here, naming it, rather than
      // store a reference to nothing and let the engine fail later somewhere
      // that hides the real cause.
      throw new Error(`Compositor ${kind.type} layer ${layerId} has no resource, content, file, or input`);
    }
    const resource = { type: kind.type, content } as Resource;

    let id: ResourceId;
    if (content.type === 'input') {
      const wired = wires.get(content.data);
      if (wired !== undefined) {
        // Replace in place rather than adding beside it, so the wire keeps one
        // entry and the layers already pointing at it stay correct.
        id = wired;
      } else {
        id = nextId();
      }
      wires.set(content.data, id);
    } else {
      const shared = Object.entries(resources).find(([, existing]) => holdsSameFile(existing, resource));
      id = shared !== undefined ? Number(shared[0]) : nextId();
    }
    resources[id] = resource;
    kind.resource = id;
    referenced.add(id);
  }

  // A wire that crossed from media to text leaves an entry with nothing left to
  // answer for, and there is no text resource to replace it with. Reconciliation
  // cannot clear it: once a text layer answers for the wire it never runs the
  // create-or-replace path again. An entry left here would read as content the
  // user took off the canvas and can put back, which it is not.
  //
  // Only an entry nothing points at is dropped. A media layer still pointing at
  // it means the document is mid-transition, and dropping it would leave that
  // layer showing nothing at all.
  for (const [id, resource] of Object.entries(resources)) {
    const wire = resource.content.type === 'input' ? resource.content.data : undefined;
    if (wire !== undefined && textWires.has(wire) && !referenced.has(Number(id))) {
      delete resources[Number(id)];
    }
  }

  return resources;
}
